import { Link } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { useEffect, useRef } from 'react';

export default function Pricing() {
  const { t } = useLanguage();
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const plan = t('pricing.plan');

  return (
    <section id="pricing" ref={sectionRef} className="section-padding bg-navy-50/50 relative">
      <div className="w-full">
        {/* Header */}
        <div className="text-center mb-16 reveal">
          <span className="inline-block px-4 py-1.5 rounded-full bg-brand-50 border border-brand-200 text-brand-600 text-sm font-semibold tracking-wider mb-6">
            {t('pricing.badge')}
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-navy-800">
            {t('pricing.title')}{' '}
            <span className="text-brand-500">{t('pricing.titleHighlight')}</span>
          </h2>
          <p className="mt-6 text-xl text-navy-400 max-w-3xl mx-auto">
            {t('pricing.subtitle')}
          </p>
        </div>

        {/* Single Plan Card */}
        <div className="reveal max-w-2xl mx-auto">
          <div className="bg-white border-2 border-navy-700 rounded-3xl p-10 lg:p-14 shadow-xl shadow-navy-700/5 relative overflow-hidden">
            {/* Corner accent */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-500/5 rounded-bl-full"></div>
            
            <div className="relative">
              <div className="text-center mb-10">
                <h3 className="text-2xl font-bold text-navy-800 mb-2">{plan?.name}</h3>
                <p className="text-navy-400 mb-8">{plan?.desc}</p>
                <div className="bg-brand-50 border border-brand-200 rounded-xl px-6 py-4">
                  <p className="text-brand-700 font-semibold text-lg">{plan?.pricingCta || 'Fill the form to become a client and get the pricing'}</p>
                </div>
              </div>

              {/* Features grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                {plan?.features?.map((feature, fIndex) => (
                  <div key={fIndex} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center mt-0.5">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-navy-600">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <Link to="/#get-started" className="block w-full text-center btn-primary !py-4 text-lg">
                {plan?.cta}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
